/**
 * The status chip for an uploaded dataset.
 *
 * A running import gets a spinner beside the word, because "Importing" on its
 * own is indistinguishable from a screen that has stopped updating — and this
 * one really is moving, on a poll. Every other status gets the Badge's dot
 * instead, so the four read as one family of status pills (slice 66, the
 * Blueprint's pill skin) and the moving one is the one without a still mark.
 *
 * ## The tone is decided here, on the Blueprint status mapping
 *
 * Slice 66 put every status chip in the product on one mapping: something
 * waiting on *you* is `warn`, finished is `good`, broken is `bad`, and a thing
 * that is merely in progress keeps `info`. For a dataset that moves exactly one
 * tone: **"Awaiting mapping" goes from neutral to warn**, because a list in
 * that state has imported nothing and will import nothing until somebody
 * confirms its columns — it is the one status in the table that is asking for
 * an action, and neutral grey said "nothing to see here" about it.
 *
 * The map lives in this file rather than in `lib/dataset-display.ts` because
 * this badge is the only thing that draws a dataset status. That table used to
 * carry a `tone` field of its own, which said neutral for "Awaiting mapping"
 * while this badge said warn; it was deleted in the same slice, so the table is
 * words only and there is one colour per status. `Record<DatasetStatus, …>`
 * keeps it total: a fifth status does not compile until somebody decides its
 * colour.
 */
import type { DatasetStatus } from '@wend/shared';
import { Badge, Spinner, type BadgeTone } from '@wend/ui';

import { DATASET_STATUS_DISPLAY } from '../lib/dataset-display';

const DATASET_BADGE_TONE: Readonly<Record<DatasetStatus, BadgeTone>> = {
  pending: 'warn',
  processing: 'info',
  ready: 'good',
  failed: 'bad',
};

export interface DatasetStatusBadgeProps {
  status: DatasetStatus;
}

export function DatasetStatusBadge({ status }: DatasetStatusBadgeProps) {
  const display = DATASET_STATUS_DISPLAY[status];
  const running = status === 'processing';

  return (
    <Badge tone={DATASET_BADGE_TONE[status]} dot={!running}>
      {running && <Spinner size="sm" label={null} />}
      {display.label}
    </Badge>
  );
}
